"use client";

// src/app/global-error.tsx
// Replaces the root layout when it crashes, so it renders its own
// <html> and <body> with self-contained markup: no layout providers, fonts or
// shared components that could fail again. The PostHog gate lives in that
// layout, so `capture` is a no-op here unless PostHog had already started.
import { useEffect, useState } from "react";
import { readTranslated } from "@/lib/posthog/translation";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Read before this boundary's own <html> commits, not later in an effect:
  // `translation.ts` is a pure, dependency-free module (no posthog-js), so a
  // static import here cannot take the fallback page down.
  const [translated] = useState(() => readTranslated());

  useEffect(() => {
    // Imported lazily, and only in the effect: a static import would put
    // posthog-js in this boundary's own chunk, so a crash caused by loading
    // the SDK would take the fallback page down with it. A failed import here
    // costs the event, not the page.
    void import("@/lib/posthog/error-events")
      .then((m) => m.captureErrorBoundary(error, window.location.pathname, translated))
      .catch(() => {});
  }, [error, translated]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily: "system-ui, sans-serif",
          color: "#111827",
          background: "#ffffff",
        }}
      >
        <main
          id="main"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "2rem",
            textAlign: "center",
            boxSizing: "border-box",
          }}
        >
          <h1 style={{ fontSize: "1.125rem", fontWeight: 600, margin: 0 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: "0.875rem", color: "#4b5563" }}>
            The application failed to load. Please try again.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              minHeight: "44px",
              minWidth: "44px",
              padding: "0 1rem",
              borderRadius: "0.375rem",
              border: "1px solid #111827",
              background: "#111827",
              color: "#ffffff",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </main>
      </body>
    </html>
  );
}
